import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Bookmark, Bell, BellOff, Trash2, Search, Loader2
} from 'lucide-react';
import api from '../../utils/api';


function getPatientToken() {
  return '';
}

export default function SavedSearchesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSearches = useCallback(async () => {
    const token = getPatientToken();
    if (!token) return;
    try {
      const res = await api.get(`/patient/saved-searches`, {
      });
      const data = res.data;
      if (data.success) setSearches(data.saved_searches || []);
    } catch (err) {
      console.error('Failed to fetch saved searches:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSearches();
  }, [isAuthenticated, navigate, fetchSearches]);

  const handleToggle = async (id) => {
    const token = getPatientToken();
    try {
      const res = await api.post(`/patient/saved-searches/${id}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({}),
      });
      const data = res.data;
      if (data.success) {
        setSearches(prev => prev.map(s =>
          s.id === id ? { ...s, notify_mode: data.notify_mode } : s
        ));
      }
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  };

  const handleDelete = async (id) => {
    const token = getPatientToken();
    try {
      const res = await api.delete(`/patient/saved-searches/${id}`, {
        method: 'DELETE',
      });
      const data = res.data;
      if (data.success) {
        setSearches(prev => prev.filter(s => s.id !== id));
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="saved-searches-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-cyan-800 text-white">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/find-clinics')}
              className="text-white hover:bg-white/10"
              data-testid="back-to-find-clinics"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">My Saved Searches</h1>
              <p className="text-sm text-teal-200">{searches.length} saved search{searches.length !== 1 ? 'es' : ''}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {searches.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Bookmark className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-slate-700 mb-1">No saved searches yet</h3>
              <p className="text-xs text-slate-500 mb-4">
                Use "Find Me Clinics" to search, then save your criteria for daily email alerts.
              </p>
              <Button
                size="sm"
                onClick={() => navigate('/find-clinics')}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="go-find-clinics-btn"
              >
                <Search className="h-3.5 w-3.5 mr-1.5" />
                Find Clinics
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="saved-searches-list">
            {searches.map(s => (
              <Card key={s.id} className="hover:border-teal-200 transition-colors" data-testid={`saved-search-${s.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Bookmark className="h-4 w-4 text-teal-600 shrink-0" />
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {s.name || s.treatment_slug.replace(/_/g, ' ')}
                        </span>
                        {s.country && (
                          <Badge variant="outline" className="text-[10px] shrink-0">{s.country}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 ml-6">
                        Saved {formatDateLocale(s.created_at_utc)}
                        {s.last_notified_at_utc && (
                          <> &middot; Last emailed {formatDateLocale(s.last_notified_at_utc)}</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${s.notify_mode === 'daily' ? 'text-teal-600' : 'text-slate-400'}`}
                        onClick={() => handleToggle(s.id)}
                        data-testid={`toggle-notify-${s.id}`}
                        title={s.notify_mode === 'daily' ? 'Notifications on (daily)' : 'Notifications off'}
                      >
                        {s.notify_mode === 'daily' ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-500"
                        onClick={() => handleDelete(s.id)}
                        data-testid={`delete-search-${s.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
